import { ColumnDef } from '@tanstack/react-table';
import Tippy from '@tippyjs/react';
import { useState } from 'react';
import { Badge, Stack } from 'react-bootstrap';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../context/ToastContext';
import {
  useActivateRegistrationMutation,
  useDeleteRegistrationMutation
} from '../../../services/endpoints/content/registrations';
import { registration } from '../../../types/registrations.type';
import { getErrorMessage } from '../../utils';

const RegistrationStatusButton: React.FC<Pick<registration, 'id' | 'active_status'>> = ({
  id,
  active_status
}) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteRegistration, { isLoading: isDeactivating }] = useDeleteRegistrationMutation();
  const [activateRegistration, { isLoading: isActivating }] =
    useActivateRegistrationMutation();
  const { addToast } = useToast();
  const isInactive = active_status === 'inactive';
  const action = isInactive ? 'Reactivate' : 'Deactivate';

  const handleStatusChange = async () => {
    try {
      if (isInactive) {
        await activateRegistration(id).unwrap();
      } else {
        await deleteRegistration(id).unwrap();
      }
      addToast(`Registration ${action.toLowerCase()}d successfully`, 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        {action}
      </button>
      <ConfirmationModal
        show={showModal}
        title={`${action} Registration`}
        message={`Are you sure you want to ${action.toLowerCase()} this registration?`}
        confirmText={`Yes, ${action}`}
        cancelText="Cancel"
        onConfirm={handleStatusChange}
        onCancel={() => setShowModal(false)}
        isLoading={isDeactivating || isActivating}
      />
    </>
  );
};

export const registrationsColumns: ColumnDef<registration>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/registrations/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Order',
    accessorKey: 'order_id',
    cell: ({ getValue }) => (
      <LinkCell
        content={`Order #${getValue<number>()}`}
        url={`/admin/orders/orders/${getValue<number>()}`}
      />
    )
  },

  {
    header: 'Parent',
    accessorKey: 'parent',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.order.parent?.full_name}
        url={`/admin/sfc-users/parents/${row.original.order.parent?.id}`}
      />
    )
  },
  {
    header: 'Parent Email',
    accessorKey: 'parent_email',
    cell: ({ row }) => <>{row.original.order.parent?.email}</>
  },
  {
    header: 'Child',
    accessorKey: 'child',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.child?.full_name}
        url={`/admin/sfc-users/children/${row.original.child?.id}`}
      />
    )
  },
  {
    header: 'Session',
    accessorKey: 'session_title',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.course?.title}
        url={`/admin/schedule/sessions/${row.original.course?.id}`}
      />
    )
  },
  {
    header: 'Subtotal',
    accessorKey: 'subtotal',
    cell: ({ row }) => <>$ {row.original.order.total}</>
  },
  {
    header: () => (
      <span>
        Refunds
        <br></br>
        <Tippy
          content={
            <span style={{ backgroundColor: '#000000', color: '#ffffff' }}>
              The maximum allowed refund is for the order as a whole, not the specific registration.
              The amount in parentheses represents the maximum allowed refund minus a 5%
              cancellation fee.
            </span>
          }
        >
          <i className="fa fa-info-circle" />
        </Tippy>
      </span>
    ),
    accessorKey: 'refunds',
    cell: ({ row }) => (
      <>
        <LinkCell
          content={`${row.original.order.refunds.length} Refunds: $${row.original.order.refunds.reduce((total, refund) => total + refund.total, 0)}`}
          url={`/admin/orders/refunds?order_id=${row.original.order_id}`}
        />
        <br></br>
        <>
          Max Allowed: ${row.original.order.maximum_refund_allowed} ($
          {row.original.order.maximum_refund_allowed_with_fee})
        </>
      </>
    )
  },
  {
    header: 'Discount',
    accessorKey: 'discount',
    cell: ({ row }) => <>{row.original.order.promotional_code ?? 'None'}</>
  },
  {
    header: 'Promotional Code',
    accessorKey: 'promotional_code',
    cell: ({ row }) => <>{row.original.order.promotional_code ?? 'None'}</>
  },
  {
    header: 'Active Status',
    accessorKey: 'active_status',
    cell: ({ row }) => {
      const isActive = row.original.active_status === 'active';
      return <Badge bg={isActive ? 'primary' : 'light'}>{isActive ? 'Active' : 'Inactive'}</Badge>;
    }
  },

  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="Show"
          url={`/admin/registrations/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Transfer"
          url={`/admin/registrations/${row.original.id}/edit`}
          underline
        />
        <RegistrationStatusButton
          id={row.original.id}
          active_status={row.original.active_status}
        />
      </Stack>
    )
  }
];
